import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Botao, Cartao, Selo, Vazio, Modal, AreaDeTexto, Campo } from "@notes/ui";
import { ShieldCheck, Quote, Check, X, Mic, ChevronDown } from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { usarAprovacoes, usarDecidirAcao, mensagemDeErro } from "../lib/api.js";
import { marcaDeTempo } from "../lib/formato.js";
import { rotuloDaAcao } from "./Reuniao.js";

/**
 * A CENTRAL DE APROVAÇÕES.
 *
 * Cada item mostra o que vai acontecer no CRM, por que a inteligência propôs e
 * o trecho da conversa que sustenta. A evidência fica ABERTA por padrão: com
 * ela escondida atrás de um clique, a pessoa aprende a aprovar sem ler, e o
 * controle deixa de existir mesmo estando na tela.
 *
 * Não existe "aprovar tudo". Aprovar em lote é a forma mais rápida de
 * transformar seis decisões em um clique distraído.
 */
export function Aprovacoes() {
  const q = usarAprovacoes();
  const decidir = usarDecidirAcao();
  const [recusando, setRecusando] = useState<{ id: string; titulo: string } | null>(null);
  const [motivo, setMotivo] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);

  if (q.isLoading) return <Pagina titulo="Aprovações"><Carregando tipo="cartoes" /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="Aprovações"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }

  const acoes = q.data?.acoes ?? [];

  const aprovar = async (id: string) => {
    setAviso(null);
    const r = await decidir.mutateAsync({ acaoId: id, decisao: "aprovada" });
    if (!r.ok) setAviso(r.motivo);
  };

  const recusar = async () => {
    if (!recusando) return;
    setAviso(null);
    const r = await decidir.mutateAsync({
      acaoId: recusando.id, decisao: "rejeitada",
      ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
    });
    setRecusando(null);
    setMotivo("");
    if (!r.ok) setAviso(r.motivo);
  };

  return (
    <Pagina
      titulo="Aprovações"
      descricao="O que a inteligência sugeriu e espera a sua decisão antes de acontecer no CRM."
      acao={acoes.length > 0 ? <Selo tom="atencao">{acoes.length} esperando</Selo> : undefined}
    >
      {aviso && <Secao ordem={0}><Erro mensagem={aviso} /></Secao>}

      {acoes.length === 0 ? (
        <Vazio
          titulo="Nada esperando você"
          descricao="Quando uma conversa gerar algo que precise da sua confirmação, aparece aqui, com o trecho que sustenta a sugestão."
        />
      ) : (
        <div className="enotes-pilha">
          {acoes.map((a, i) => {
            const evidencias = (a.evidencias ?? []) as Array<{ inicioMs: number; trecho: string }>;
            const sensivel = a.nivel === "sensivel";
            const campos = (a.campos ?? {}) as Record<string, unknown>;
            return (
              <Cartao key={a.id} className="surge" style={{ animationDelay: `${i * 55}ms` }}>
                <div className="enotes-aprov-topo">
                  <span className="enotes-cartao-icone" aria-hidden><ShieldCheck size={15} /></span>
                  <div className="enotes-aprov-texto">
                    <p className="enotes-aprov-titulo">{rotuloDaAcao(a.tipo)}</p>
                    <p className="enotes-aprov-justificativa">{a.justificativa}</p>
                  </div>
                  <Selo tom={sensivel ? "critico" : "neutro"}>
                    {sensivel ? "precisa da sua confirmação" : "operacional"}
                  </Selo>
                </div>

                {/* O que MUDA no CRM, em português, antes de qualquer botão. */}
                <PreVisualizacao tipo={a.tipo} campos={campos} />

                {evidencias.length > 0 && (
                  <div className="enotes-aprov-evidencias">
                    {evidencias.slice(0, 3).map((e, n) => (
                      <p key={n} className="enotes-evidencia" data-estatica="sim">
                        <Quote size={12} aria-hidden />
                        <span className="carimbo">{marcaDeTempo(e.inicioMs)}</span>
                        <span className="enotes-evidencia-texto">{e.trecho}</span>
                      </p>
                    ))}
                    {evidencias.length > 3 && (
                      <p className="carimbo">mais {evidencias.length - 3} trecho{evidencias.length - 3 === 1 ? "" : "s"} na conversa</p>
                    )}
                  </div>
                )}

                <div className="enotes-aprov-rodape">
                  {a.reuniaoId && (
                    <Link to="/reunioes/$id" params={{ id: a.reuniaoId }} className="enotes-linha-link">
                      <Mic size={13} aria-hidden /> ouvir a conversa inteira
                    </Link>
                  )}
                  <span className="enotes-aprov-botoes">
                    <Botao tamanho="sm" variante="fantasma"
                      onClick={() => { setRecusando({ id: a.id, titulo: rotuloDaAcao(a.tipo) }); setMotivo(""); }}>
                      <X size={14} /> Recusar
                    </Botao>
                    <Botao tamanho="sm" variante="primario" ocupado={decidir.isPending}
                      onClick={() => void aprovar(a.id)}>
                      <Check size={14} /> Aprovar
                    </Botao>
                  </span>
                </div>
              </Cartao>
            );
          })}
        </div>
      )}

      <Modal
        aberto={!!recusando}
        aoFechar={() => setRecusando(null)}
        titulo="Recusar esta ação"
        descricao={recusando?.titulo}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(null)}>Voltar</Botao>
            <Botao variante="perigo" ocupado={decidir.isPending} onClick={() => void recusar()}>Recusar</Botao>
          </>
        }
      >
        <Campo rotulo="Por que não" dica="O motivo ensina a inteligência a propor melhor. Pode deixar em branco.">
          <AreaDeTexto value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: o cliente pediu para não registrar isso" maxLength={500} />
        </Campo>
      </Modal>
    </Pagina>
  );
}

/**
 * O que a ação MUDA, em português. Sem isto, a pessoa aprova um rótulo
 * ("Criar tarefa no CRM") sem saber qual tarefa, com que prazo, para quem.
 */
function PreVisualizacao({ tipo, campos }: { tipo: string; campos: Record<string, unknown> }) {
  const linhas = LEITURA_DOS_CAMPOS[tipo]?.(campos) ?? Object.entries(campos)
    .filter(([, v]) => v != null && v !== "")
    .slice(0, 5)
    .map(([k, v]) => [rotuloDeCampo(k), String(v)] as const);

  if (linhas.length === 0) return null;
  return (
    <dl className="enotes-aprov-previa">
      {linhas.map(([rotulo, valor]) => (
        <div key={rotulo}>
          <dt>{rotulo}</dt>
          <dd>{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

const texto = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

const LEITURA_DOS_CAMPOS: Record<string, (c: Record<string, unknown>) => Array<readonly [string, string]>> = {
  "crm.nota.criar": (c) => {
    const t = texto(c["texto"]) ?? texto(c["body"]);
    return t ? [["Nota que será criada", t] as const] : [];
  },
  "crm.tarefa.criar": (c) => [
    ...(texto(c["titulo"]) ? [["Tarefa", texto(c["titulo"])!] as const] : []),
    ...(texto(c["vencimento"]) ? [["Prazo", texto(c["vencimento"])!] as const] : []),
    ...(texto(c["descricao"]) ? [["Detalhe", texto(c["descricao"])!] as const] : []),
  ],
  "crm.negociacao.marcar_perdida": (c) => [
    ["O que acontece", "A negociação é marcada como perdida no CRM, com os mesmos efeitos de fazer isso na tela."] as const,
    ...(texto(c["motivo"]) ? [["Motivo", texto(c["motivo"])!] as const] : []),
  ],
};

const rotuloDeCampo = (k: string): string => ({
  texto: "Texto", titulo: "Título", descricao: "Descrição", vencimento: "Prazo",
  motivo: "Motivo", valor: "Valor", campo: "Campo",
}[k] ?? k);
